import React from "react";
import moment from "moment";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import _ from "lodash";
import { FormattedMessage } from "react-intl";

import { PictureAsPdf, ArrowBack } from "components/Icons";
import Button from "components/Button";
import OrderLines from "./OrderLines";
import OrderDeliveries from "./OrderDeliveries";
import OrderReceiptsReturns from "./OrderReceiptsReturns";
import OrderInvoices from "./OrderInvoices";
import OrderVersions from "./OrderVersions";
import CompanyDetailsCard from "./CompanyDetailsCard";
import OrderDetailsCard from "./OrderDetailsCard";
import { connect } from "../../../reduxConnector";
import OrderActions from "../actions";
import { downloadDocument } from "helpers/download";
import { URLS } from "constants/urlConstants";

import "./style.scss";

interface IProps {
  roles: string[];
  getOrder: (id: number, roleId: string) => void;
  getOrderLines: (id: number) => void;
  getOrderDeliveries: (id: number, roleId: string) => void;
  getOrderReceiptsReturns: (id: number, roleId: string) => void;
  getOrderInvoices: (id: number, roleId: string) => void;
  getOrderVersions: (id: number, roleId: string) => void;
  markAsDelivered: (ids: number[], orderId: number) => void;
  currentItem: any;
  isLoading: boolean;
  orderLines: any;
  orderDeliveries: any;
  orderReceiptsReturns: any;
  orderInvoices: any;
  orderVersions: any;
  match: any;
  history: any;
  location: any;
}

class OrderDetails extends React.Component<IProps> {
  public componentDidMount() {
    const {
      match: {
        params: { id }
      },
      location: { search },
      getOrder
    } = this.props;

    getOrder(id, search);
  }

  private onActionSelect = (ids, selectedOption) => {
    const {
      markAsDelivered,
      currentItem: { Id }
    } = this.props;

    selectedOption.value === "delivered" && markAsDelivered(ids, Id);
  };

  public render() {
    const {
      roles,
      isLoading,
      orderLines,
      getOrderLines,
      orderDeliveries,
      getOrderDeliveries,
      orderReceiptsReturns,
      getOrderReceiptsReturns,
      orderInvoices,
      getOrderInvoices,
      getOrderVersions,
      orderVersions,
      currentItem,
      history
    } = this.props;

    if (!currentItem) {
      return null;
    }

    const orderLinesWithRoleId = _.map(orderLines, item => ({
      ...item,
      RoleId: currentItem.RoleId
    }));

    return (
      <div id="order-details-page">
        <h1>
          <ArrowBack onClick={() => history.back()} />
          <FormattedMessage id="ORDERS_DETAILS" />
        </h1>
        <div className="title-raw">
          <div className="title-raw-info">
            <div>
              <FormattedMessage id="ORDERS_NUMBER" tagName="h3" />
              <h2>{currentItem.Number}</h2>
            </div>
            <div>
              <FormattedMessage id="ORDERS_DATE" tagName="h3" />
              <h2>{moment(currentItem.Date).format("DD-MM-YYYY")}</h2>
            </div>
            <div>
              <FormattedMessage id="VERSION" tagName="h3" />
              <h2>{currentItem.VersionNumber}</h2>
            </div>
            <div>
              <FormattedMessage id="STATUS" tagName="h3" />
              <h2>{currentItem.StateId}</h2>
            </div>
          </div>
        </div>

        <OrderDetailsCard currentItem={currentItem} />

        <CompanyDetailsCard currentItem={currentItem} />

        <div className="download-button-wrapper">
          <Button
            id={`download-order-${_.get(
              currentItem,
              "DocumentId",
              "no_document"
            )}.pdf`}
            icon={<PictureAsPdf />}
            onClick={() => downloadDocument(currentItem)}
          >
            ORDERS_DOWNLOAD
          </Button>
        </div>

        <Tabs>
          <TabList>
            <Tab>
              <FormattedMessage id="ORDERS_LINES" />
            </Tab>
            <Tab>
              <FormattedMessage id="DELIVERIES_HEADER" />
            </Tab>
            <Tab>
              <FormattedMessage id="ORDERS_RECEIPTS" />
            </Tab>
            {_.includes(roles, "Financieel") && (
              <Tab>
                <FormattedMessage id="INVOICES" />
              </Tab>
            )}
            <Tab>
              <FormattedMessage id="VERSIONS" />
            </Tab>
          </TabList>
          <hr />
          <TabPanel>
            <OrderLines
              isLoading={isLoading}
              data={orderLinesWithRoleId}
              loadData={() => getOrderLines(currentItem.Id)}
              onActionSelect={this.onActionSelect}
            />
          </TabPanel>
          <TabPanel>
            <OrderDeliveries
              isLoading={isLoading}
              data={orderDeliveries}
              loadData={() =>
                getOrderDeliveries(currentItem.Id, currentItem.RoleId)
              }
              onRowClick={rowData =>
                history.push(
                  `${URLS.DELIVERIES_URL}/${rowData.Id}?kooptype=${
                    rowData.RoleId
                  }`
                )
              }
            />
          </TabPanel>
          <TabPanel>
            <OrderReceiptsReturns
              isLoading={isLoading}
              data={orderReceiptsReturns}
              loadData={() =>
                getOrderReceiptsReturns(currentItem.Id, currentItem.RoleId)
              }
            />
          </TabPanel>
          {_.includes(roles, "Financieel") && (
            <TabPanel>
              <OrderInvoices
                isLoading={isLoading}
                data={orderInvoices}
                loadData={() =>
                  getOrderInvoices(currentItem.Id, currentItem.RoleId)
                }
                onRowClick={rowData =>
                  history.push(`${URLS.INVOICES_URL}/${rowData.Id}`)
                }
              />
            </TabPanel>
          )}
          <TabPanel>
            <OrderVersions
              isLoading={isLoading}
              data={orderVersions}
              loadData={() =>
                getOrderVersions(currentItem.Id, currentItem.RoleId)
              }
            />
          </TabPanel>
        </Tabs>
      </div>
    );
  }
}

export default connect(
  state => ({
    roles: state.app.roles,
    currentItem: state.orders.currentItem,
    isLoading: state.orders.isLoading,
    orderLines: state.orders.orderLines,
    orderDeliveries: state.orders.orderDeliveries,
    orderReceiptsReturns: state.orders.orderReceiptsReturns,
    orderInvoices: state.orders.orderInvoices,
    orderVersions: state.orders.orderVersions
  }),
  {
    getOrder: OrderActions.getOrder,
    getOrderLines: OrderActions.getOrderLines,
    getOrderDeliveries: OrderActions.getOrderDeliveries,
    getOrderReceiptsReturns: OrderActions.getOrderReceiptsReturns,
    getOrderInvoices: OrderActions.getOrderInvoices,
    getOrderVersions: OrderActions.getOrderVersions,
    markAsDelivered: OrderActions.markAsDelivered
  }
)(OrderDetails);
